/**
 * Save Calculation Section Component
 *
 * Provides UI for saving calculations.
 * Shows different states for authenticated vs unauthenticated users.
 *
 * Features:
 * - Authentication state detection
 * - Save/Update calculation functionality
 * - Navigation to login/signup pages
 * - Success/error message display
 */

import {
  Paper,
  Stack,
  Text,
  Button,
  Alert,
  Group,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconUserPlus,
  IconLogin,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import type { CalculatorFormValues, CalculationResults } from '../../../../types/calculator';

/**
 * Component props interface
 */
interface SaveCalculationSectionProps {
  /** Calculator input data */
  calculationData: CalculatorFormValues;

  /** Calculation results */
  calculationResults: CalculationResults;

  /** Callback when user saves calculation */
  onSave: () => void;

  /** Whether save operation is in progress */
  isSaving?: boolean;

  /** Error message from save operation */
  saveError?: string;

  /** Whether save was successful */
  saveSuccess?: boolean;

  /** Whether calculation already exists for this year */
  calculationExists?: boolean;
}

/**
 * Save Calculation Section Component
 *
 * Displays different UI based on authentication state:
 * - Unauthenticated: Shows signup/login buttons
 * - Authenticated: Shows save button
 */
export function SaveCalculationSection({
  onSave,
  isSaving = false,
  saveError,
  saveSuccess = false,
  calculationExists = false,
}: SaveCalculationSectionProps) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  /**
   * Handle save button click
   * Calls parent callback
   */
  const handleSave = () => {
    onSave();
  };

  /**
   * Handle signup button click
   * Navigate to signup page
   */
  const handleSignup = () => {
    void navigate('/signup');
  };

  /**
   * Handle login button click
   * Navigate to login page
   */
  const handleLogin = () => {
    void navigate('/login');
  };

  return (
    <Paper
      p="xl"
      shadow="sm"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-blue-3)',
        backgroundColor: 'var(--mantine-color-blue-0)',
      }}
    >
      <Stack gap="md">
        {/* Header with Icon */}
        <Group gap="sm">
          <IconDeviceFloppy size={28} color="var(--mantine-color-blue-6)" />
          <Text size="xl" fw={600} c="blue.8">
            Zapisz swoją kalkulację
          </Text>
        </Group>

        {/* Description - Different for authenticated vs unauthenticated */}
        {!isAuthenticated && (
          <Text size="sm" c="dimmed">
            Zarejestruj się, aby zapamiętać swoje kalkulacje na przyszłość.
          </Text>
        )}

        {isAuthenticated && (
          <Text size="sm" c="dimmed">
            Zapisz swoją kalkulację, aby móc wrócić do niej później.
          </Text>
        )}

        {/* Success Message */}
        {saveSuccess && (
          <Alert
            icon={<IconCheck size={18} />}
            color="green"
            variant="light"
            title={calculationExists ? "Kalkulacja zaktualizowana" : "Kalkulacja zapisana"}
          >
            <Text size="sm">
              Twoja kalkulacja została pomyślnie {calculationExists ? 'zaktualizowana' : 'zapisana'}.
            </Text>
          </Alert>
        )}

        {/* Error Message */}
        {saveError && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            variant="light"
            title="Błąd podczas zapisywania"
          >
            <Text size="sm">{saveError}</Text>
          </Alert>
        )}

        {/* Unauthenticated State */}
        {!isAuthenticated && (
          <Group gap="md" mt="xs">
            <Button
              leftSection={<IconUserPlus size={18} />}
              onClick={handleSignup}
              variant="filled"
              color="blue"
              size="md"
            >
              Zarejestruj się
            </Button>
            <Button
              leftSection={<IconLogin size={18} />}
              onClick={handleLogin}
              variant="outline"
              color="blue"
              size="md"
            >
              Zaloguj się
            </Button>
          </Group>
        )}

        {/* Authenticated State */}
        {isAuthenticated && (
          <Stack gap="md">
            {/* Save Button */}
            <Group gap="md">
              <Button
                leftSection={<IconDeviceFloppy size={18} />}
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
                variant="filled"
                color="blue"
                size="md"
              >
                {calculationExists ? 'Zaktualizuj kalkulację' : 'Zapisz kalkulację'}
              </Button>
            </Group>

            {/* Information about saved data */}
            <Text size="xs" c="dimmed">
              Zapisujemy Twoje dane zużycia, wybraną taryfę oraz wyniki porównania
              dla roku {new Date().getFullYear()}.
            </Text>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
